import {
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
} from '@apollo/orbit';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router';

import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { referenceRouteConfig } from 'src/app/graph/routes';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { useQueryParam } from 'src/hooks/useQueryParam';
import Config from 'src/lib/config';
import { DocsRouter } from 'src/lib/routers';

import { Form } from '../common/form/Form';
import { CloseModalFn } from '../common/URLModal';
import { ErrorMessage } from '../errorMessage/ErrorMessage';

import { PublishYourSchema } from './PublishYourSchema';
import { PublishModeTab } from './shared';
import { usePublishedSchema } from './usePublishedSchema';

interface Props {
  isOpen: boolean;
  /**
   * User interacted in some way to close the modal
   */
  onClose: CloseModalFn;
  __test?: {
    initialTab?: PublishModeTab;
    staging?: boolean;
  };
}

export const PublishYourSchemaModal = ({ isOpen, onClose, __test }: Props) => {
  const {
    graphId: graphIdFromRouteParams,
    graphVariant: graphVariantFromRouteParams,
  } = useGraphRef() ?? {};

  const graphIdFromQueryParams = useQueryParam('graphId');
  const graphVariantFromQueryParams = useQueryParam('variant');
  const architectureFromRoute = useQueryParam('publishArch');
  const publishUpdate = useQueryParam('publishUpdate');

  const graphId = graphIdFromRouteParams ?? graphIdFromQueryParams;
  const graphVariant =
    graphVariantFromRouteParams ??
    graphVariantFromQueryParams ??
    Config.service.defaultVariant;

  const { data, loading, error, architecture, published } = usePublishedSchema({
    graphId,
    graphVariant,
  });
  const history = useHistory();

  const handleClose = useCallback(
    (event?: Event) => {
      // make sure to clear the old state from the URL on close.
      onClose(event, [
        Config.queryParameters.GraphId,
        Config.queryParameters.PublishSchemaArch,
        Config.queryParameters.PublishSchemaUpdate,
      ]);
    },
    [onClose],
  );

  const federated =
    architectureFromRoute === 'federation' || architecture === 'federation';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="3xl">
      <ModalOverlay />
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          handleClose();
        }}
        noValidate
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {loading
                ? ''
                : `${publishUpdate ? 'Update schema' : 'Publish your schema'}${
                    federated ? ' using Federation' : ''
                  }`}
            </ModalTitle>
            <ModalDescription>
              {loading || error || !data?.graph ? null : federated ? (
                <>
                  To learn more about managed federation, visit the{' '}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    to={DocsRouter.path('ManagedFederation')}
                  >
                    Apollo docs
                  </Link>
                  .
                </>
              ) : !published && !publishUpdate ? (
                <>Pick one of these options. You can always change it later.</>
              ) : null}
            </ModalDescription>
          </ModalHeader>
          {/* TODO(rebrand-rollout)
           * Remove these classes when <PublishYourSchema /> can be refactored
           * or split out to use <ModalFooter />. This component is also used
           * in a card so we have to override some styles for this modal case to
           * get it to space nicely without affecting the card.
           */}
          <ModalBody className="pb-6 [&>.mb-4]:mb-0 [&>.mt-4]:mt-0 [&>pre]:flex-shrink-0">
            {error ? (
              <ErrorMessage className="mb-10" error={error} />
            ) : loading ? (
              <div className="flex items-center justify-center">
                <Spinner />
              </div>
            ) : data?.graph && graphId ? (
              <PublishYourSchema
                graph={data.graph}
                graphId={graphId}
                graphVariant={graphVariant}
                federated={federated}
                forUpdate={publishUpdate === 'true'}
                onComplete={() =>
                  history.push(
                    referenceRouteConfig.path({ graphId, graphVariant }),
                  )
                }
                onClose={handleClose}
                __test={__test}
              />
            ) : (
              <NotFound
                title="Unexpected Error"
                subtitle={
                  <>
                    We've sent you somewhere we shouldn't have! Please{' '}
                    <ContactSupportLink>contact support</ContactSupportLink> and
                    let us know
                  </>
                }
              />
            )}
          </ModalBody>
        </ModalContent>
      </Form>
    </Modal>
  );
};
